/**
 * Dokumenter on the employee's own page: what the company has published, and
 * what has been filed for them. ADR 0020.
 *
 * Read only. Employees do not upload, rename, delete or make folders; that is
 * the administrator's, behind `documents.manage`, and lives on the employee card
 * (pages/Files.tsx).
 *
 * One route answers both lists, because the server decides what may be seen. The
 * previous attempt made the client name a folder, and every client named the
 * literal string "user", so all employees shared one folder and each of them was
 * refused anyway (web-employees.md §8).
 */
import { errorText } from '@/api/client';
import { type ApiDocument, downloadDocument, useMyDocuments } from '@/api/data';
import { T } from '@/i18n';
import { DokumentLesere } from '@/pages/DokumentLesere';
import { Button, Empty, Panel, Skeleton, toast } from '@/ui';
import { fmtD } from '@/ui/format';
import { useState } from 'react';

const size = (n: number) =>
  !n ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} kB`;

function Row({ doc }: { doc: ApiDocument }) {
  // Only for a document that is mine: who opened it, administrators included.
  // That is decision 9, and the reason the log is worth keeping at all.
  const [readers, setReaders] = useState(false);
  const open = async () => {
    try {
      await downloadDocument(doc);
    } catch (e) {
      toast(errorText(e));
    }
  };
  return (
    <div className="prow">
      <button type="button" className="lnk" onClick={open} aria-label={`${T('Last ned')} ${doc.name}`}>
        {doc.name}
      </button>
      <span className="pv">
        {fmtD(doc.createdAt)}
        {size(doc.size) ? ` · ${size(doc.size)}` : ''}
        {doc.scope === 'user' ? (
          <>
            {' '}
            <Button
              small
              onClick={() => setReaders(!readers)}
              aria-label={`${T('Hvem har åpnet')} ${doc.name}`}
            >
              {T('Hvem har åpnet')}
            </Button>
          </>
        ) : null}
      </span>
      {readers ? <DokumentLesere id={doc.id} /> : null}
    </div>
  );
}

export function MineDokumenter() {
  const q = useMyDocuments();
  const shared = q.data?.shared ?? [];
  const mine = q.data?.mine ?? [];

  return (
    <Panel title={T('Dokumenter')} sub={T('fra bedriften, og det som er lagt inn for deg')}>
      {q.isLoading ? (
        <Skeleton />
      ) : q.isError ? (
        // A blink in the network is not "you have no documents".
        <Empty>{T('Fikk ikke hentet dokumentene.')}</Empty>
      ) : (
        <>
          <div className="prow">
            <b>{T('Fra bedriften')}</b>
            <span className="pv" />
          </div>
          {shared.length ? (
            shared.map((d) => <Row key={d.id} doc={d} />)
          ) : (
            <Empty>{T('Ingen dokumenter er publisert ennå.')}</Empty>
          )}
          <div className="prow">
            <b>{T('Mine dokumenter')}</b>
            <span className="pv" />
          </div>
          {mine.length ? (
            mine.map((d) => <Row key={d.id} doc={d} />)
          ) : (
            <Empty>{T('Ingen dokumenter er lagt inn for deg.')}</Empty>
          )}
        </>
      )}
    </Panel>
  );
}
